import {
  Box,
  Button,
  Heading,
  Link,
  Spinner,
  Text,
  Flex,
  SimpleGrid,
  useColorModeValue,
  VStack,
  Spacer,
  Icon,
} from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import SidebarWithHeader from 'components/SidebarWithHeader';
import React from 'react';
import { IoBuildOutline } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { useGetLanderProjectsQuery } from 'store/aiHub/externalAiApi';

const BASE_W = 1280;      
const BASE_H = 700;      
const PREVIEW_H = 200;   
const scale = PREVIEW_H / BASE_H; 

function Lander() {
  const userInfo = useSelector((s) => s.auth?.userInfo);
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const textColorSub = useColorModeValue("brand.300", "brand.700");
  const borderColor = useColorModeValue("brand.300", "purple");
  const email = userInfo?.email;

  const { data, isFetching, isError, error } = useGetLanderProjectsQuery(
    email ?? skipToken
  );

  return (
    <SidebarWithHeader>
      <Box p={2}>
        {/* Header */}
        <Flex
          direction={{ base: "column", md: "row" }}
          align={{ base: "stretch", md: "start" }}
          justify="space-between"
          mb={6}
          gap={4}
        >
          <Box flex="1">
            <Heading size="lg" mb={2}>
              Canva: AI Lander
            </Heading>
            <Text fontSize={"14px"} color={textColorSub}>
              Transform ideas into production-ready interfaces instantly with AI-powered Text-to-UI, Image-to-UI, and Figma-to-UI. From simple concepts to complex applications, Canva generates beautiful, responsive designs and code in seconds.
            </Text>
            {/* {data?.deployedProjects?.length > 0 && (
                <Text fontSize="md">
                  <Text as="span" fontWeight="bold">Projects: </Text>{" "}
                  <Text as="span">{data.count ?? 0}</Text>
                </Text>
            )} */}
          </Box>

          {data?.deployedProjects?.length > 0 && (
            <Button
              as={Link}
             href={`${process.env.REACT_APP_CANVA_BASE_URL}/authenticateHustle?token=${userInfo?.token}`}
              colorScheme="blue"
              size="sm"
              isExternal
              leftIcon={<IoBuildOutline />}
              mt={{ base: 4, md: 0 }}          
            >
              Build More
            </Button>
          )}
        </Flex>

        <Box >
          {isFetching ? (
            <Flex
              direction="column"
              gap={2}
              alignItems="center"
              justifyContent="center"
              h="70vh"
            >
              <Spinner />
              <Text>Loading…</Text>
            </Flex>
          ) : isError ? (
            <Flex direction="column" gap={1} align="center" justify="center" w="100%" h="60vh">
              <Text fontSize={'sm'}>To get started, click the button below and create an account using the same email ID you’re currently logged in with.</Text>
              <Button
                mt={'10px'}
                as={Link}
                href={`${process.env.REACT_APP_CANVA_BASE_URL}/authenticateHustle?token=${userInfo?.token}`}
                colorScheme="blue"
                size="sm"
                isExternal
                width="auto"
              >
                Connect with Lander
              </Button>
            </Flex>
          ) : (
            <>
              {/* Projects Grid */}
              {(data?.deployedProjects?.length ?? 0) === 0 ? (
                <Flex direction="column" gap={1} align="center" justify="center" w="100%" h="60vh">
                  <Text fontSize={'sm'}>
                    No deployed projects found for this user. You need to build a new project.
                  </Text>
                  <Button
                    mt={'10px'}
                    as={Link}
                   href={`${process.env.REACT_APP_CANVA_BASE_URL}/authenticateHustle?token=${userInfo?.token}`}
                    colorScheme="blue"
                    size="sm"
                    isExternal
                    width="auto"
                  >
                    Connect with Lander
                  </Button>
                </Flex>
              ) : (
                <SimpleGrid columns={[1, 2, 3]} spacing={6}>
                  {data?.deployedProjects?.map((proj, idx) => (
                    <Flex
                      key={proj?.uiId || idx}
                      direction="column"
                      p={5}
                      borderWidth="1px"
                      borderRadius="lg"
                      bg={bgColor}
                      shadow="md"
                      minH="250px"
                      overflow="hidden"
                    >
                      <VStack align="start" spacing={3} flex="1" w="100%" minW={0}>
                        <Text fontWeight="semibold" minW={0} isTruncated>
                          {proj.projectName || 'Untitled Project'}
                        </Text>

                        <Text
                          noOfLines={3}
                          whiteSpace="pre-wrap"
                          wordBreak="break-word"
                          overflowWrap="anywhere"
                          minW={0}
                        >
                          {proj.prompt}
                        </Text>

                        <Spacer />

                        {/* Small iframe preview */}
                        <Box
                            as="a"
                            href={proj.deploymentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            w="100%"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            overflow="hidden"
                            position="relative"
                            _hover={{ shadow: "md", borderColor }}
                          >
                            {/* Scaled canvas */}
                            <Box
                              // container takes the preview size
                              w="100%"
                              h={`${PREVIEW_H}px`}
                              overflow="hidden"
                              position="relative"
                            >
                              {/* inner frame is rendered at BASE size, then scaled down */}
                              <Box
                                width={`${BASE_W}px`}
                                height={`${BASE_H}px`}
                                transform={`scale(${scale})`}
                                transformOrigin="top left"
                                // make it responsive in width: center it
                                position="absolute"
                                top="0"
                                left="0"
                              >
                                <iframe
                                  src={proj.deploymentUrl}
                                  title={proj.projectName}
                                  width={BASE_W}
                                  height={BASE_H}
                                  style={{
                                    border: 0,
                                    display: "block",
                                    pointerEvents: "none", // keep it non-interactive in the card
                                  }}
                                  loading="lazy"
                                />
                              </Box>
                            </Box>
                        </Box>
                      </VStack>
                    </Flex>
                  ))}
                </SimpleGrid>
              )}
            </>
          )}
        </Box>
      </Box>
    </SidebarWithHeader>
  );
}

export default Lander;
